interface TTSOptions {
    text: string;
    voiceId?: string;
    speed?: number;
    volume?: number;
    pitch?: number;
    needCache?: boolean;
}

// Using our server-side proxy with App Router format
const API_PROXY_URL = '/api/tts/minimax';

import supabase from './supabase';

export const CurrentVoiceId = 'Cute_Elf';

/**
 * Stream TTS from our server proxy to MiniMax API and convert to playable audio format
 */
export async function* streamTTS(options: TTSOptions): AsyncGenerator<{data: Uint8Array, isLastChunk: boolean}> {
    const { text, voiceId = 'Cute_Elf', speed = 1.0, volume = 1.5, pitch = -1, needCache = true } = options;

    // // Check if we already have this text in the cache
    // const { data: cacheData } = await supabase
    //     .from('tts_cache')
    //     .select('*')
    //     .eq('text', text)
    //     .maybeSingle();

    // if (cacheData?.audio_url) {
    //     // If we have a cached version, fetch it and return as a single chunk
    //     try {
    //         const response = await fetch(cacheData.audio_url);
    //         if (response.ok) {
    //             const audioData = await response.arrayBuffer();
    //             yield {
    //                 data: new Uint8Array(audioData),
    //                 isLastChunk: false
    //             };
    //             return; // Exit the generator after yielding the cached audio
    //         }
    //     } catch (error) {
    //         console.error('Error fetching cached TTS:', error);
    //         // Continue with API call if cached file can't be fetched
    //     }
    // }

    const { apiClient } = await import('@/lib/api-client');
    const response = await apiClient.post(API_PROXY_URL, {
        model: 'speech-2.6-turbo',
        text,
        stream: true,
        voice_setting: {
            voice_id: voiceId,
            speed,
            vol: volume,
            pitch: pitch,
            emotion: 'happy',
        },
        audio_setting: {
            sample_rate: 24000,
            bitrate: 128000,
            format: 'pcm',  // PCM format for direct Web Audio API processing
            channel: 1,
        }
    });

    if (!response.ok) {
        throw new Error(`Failed to fetch TTS: ${response.status} ${response.statusText}`);
    }

    const reader = response.body?.getReader();
    if (!reader) {
        throw new Error('Failed to get reader from response');
    }

    // Process the streaming response
    const decoder = new TextDecoder();
    let buffer = '';
    let finalAudioChunk: Uint8Array | null = null;

    while (true) {
        const { done, value } = await reader.read();
        if (done) break;

        buffer += decoder.decode(value, { stream: true });

        // Process completed chunks
        const lines = buffer.split('\n');
        buffer = lines.pop() || ''; // Keep the last incomplete line in the buffer

        for (const line of lines) {
            if (line.trim() === '') continue;

            try {
                if (line.startsWith('data:')) {
                    const data = JSON.parse(line.substring(5));
                    const isLastChunk = !!data.extra_info; // Check if this is the last chunk with extra_info
                    
                    if (data.data && data.data.audio) {
                        // Convert hex string to Uint8Array
                        const audioHex = data.data.audio;
                        const audioChunk = hexToUint8Array(audioHex);
                        
                        if (isLastChunk) {
                            // Save the last chunk for upload (MiniMax returns all audio in the last chunk)
                            finalAudioChunk = audioChunk;
                        }
                        
                        yield {
                            data: audioChunk,
                            isLastChunk
                        };
                        
                        // If this is the last chunk, convert to WAV and upload
                        if (isLastChunk && finalAudioChunk && needCache) {
                            // We'll handle this after the generator function runs
                            // This will be in a separate function to avoid blocking the audio player
                            saveAudioToCache(text, finalAudioChunk, voiceId);
                        }
                    }
                }
            } catch (error) {
                console.error('Error processing TTS chunk:', error);
            }
        }
    }
}

/**
 * Save the audio to R2 storage and cache the URL in Supabase
 */
async function saveAudioToCache(text: string, audioData: Uint8Array, voiceId: string): Promise<void> {
    try {
        // Create WAV file from PCM data
        const wavFile = pcmToWav(audioData, {
            sampleRate: 24000,
            channels: 1,
        });
        
        // Upload the WAV file
        const uploadResult = await uploadAudioFile(wavFile, text);
        
        if (uploadResult?.uploadUrl) {
            // Save to tts_cache table
            await supabase.from('tts_cache').upsert([
                {
                    text,
                    audio_url: uploadResult.uploadUrl,
                    created_at: new Date().toISOString(),
                    voice_id: voiceId,
                }
            ]);
            
            console.log('TTS audio cached successfully:', uploadResult.uploadUrl, voiceId);
        }
    } catch (error) {
        console.error('Failed to cache TTS audio:', error);
    }
}

/**
 * Upload the audio file to R2 storage
 */
async function uploadAudioFile(audioData: Blob, text: string): Promise<any> {
    try {
        // Get a presigned URL
        const { apiClient } = await import('@/lib/api-client');
        const presignResponse = await apiClient.post('/api/upload/presign', {
            type: 'syllabus_audio',
            filename: `tts_${text.substring(0, 20).replace(/\s+/g, '_')}.wav`
        });

        if (!presignResponse.ok) {
            throw new Error('Failed to get presigned URL');
        }

        const presignData = await presignResponse.json();

        // Upload the file using the presigned URL
        const uploadResponse = await fetch(presignData.presignedUrl, {
            method: 'PUT',
            headers: {
                'Content-Type': 'audio/wav',
            },
            body: audioData,
        });

        if (!uploadResponse.ok) {
            throw new Error('Failed to upload file');
        }

        return presignData;
    } catch (error) {
        console.error('Error uploading audio file:', error);
        throw error;
    }
}

/**
 * Convert hex string to Uint8Array
 */
function hexToUint8Array(hexString: string): Uint8Array {
    // Remove any whitespace and ensure even length
    const cleanHex = hexString.replace(/\s/g, '');
    const len = cleanHex.length;
    
    // Create buffer with the right size
    const bytes = new Uint8Array(len / 2);
    
    // Convert each hex byte to decimal
    for (let i = 0; i < len; i += 2) {
        bytes[i / 2] = parseInt(cleanHex.substring(i, i + 2), 16);
    }
    
    return bytes;
}

/**
 * Convert PCM data to WAV format
 */
function pcmToWav(pcmData: Uint8Array, options: { sampleRate: number, channels: number }): Blob {
    const { sampleRate = 24000, channels = 1 } = options;
    
    // WAV header size
    const headerSize = 44;
    
    // PCM is 16-bit, so each sample is 2 bytes
    const bitsPerSample = 16;
    const bytesPerSample = bitsPerSample / 8;
    
    // Calculate sizes for WAV header
    const dataSize = pcmData.length;
    const blockAlign = channels * bytesPerSample;
    const byteRate = sampleRate * blockAlign;
    const totalSize = headerSize + dataSize;
    
    // Create buffer for WAV file
    const buffer = new ArrayBuffer(totalSize);
    const view = new DataView(buffer);
    
    // Write WAV header
    // "RIFF" chunk descriptor
    writeString(view, 0, 'RIFF');
    view.setUint32(4, totalSize - 8, true);
    writeString(view, 8, 'WAVE');
    
    // "fmt " sub-chunk
    writeString(view, 12, 'fmt ');
    view.setUint32(16, 16, true); // fmt chunk size
    view.setUint16(20, 1, true); // audio format (1 = PCM)
    view.setUint16(22, channels, true);
    view.setUint32(24, sampleRate, true);
    view.setUint32(28, byteRate, true);
    view.setUint16(32, blockAlign, true);
    view.setUint16(34, bitsPerSample, true);
    
    // "data" sub-chunk
    writeString(view, 36, 'data');
    view.setUint32(40, dataSize, true);
    
    // Write PCM data to buffer
    const dataStartIndex = 44;
    const wavBytes = new Uint8Array(buffer);
    wavBytes.set(pcmData, dataStartIndex);
    
    // Return as Blob
    return new Blob([buffer], { type: 'audio/wav' });
}

/**
 * Helper to write ASCII strings to DataView
 */
function writeString(view: DataView, offset: number, string: string): void {
    for (let i = 0; i < string.length; i++) {
        view.setUint8(offset + i, string.charCodeAt(i));
    }
}

/**
 * Create a playable audio URL from audio chunks
 */
export function createAudioUrl(audioChunk: Uint8Array): string {
    const blob = new Blob([audioChunk as BlobPart], { type: 'audio/mp3' });
    return URL.createObjectURL(blob);
}
